import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from '../../hooks/useTranslation';
import vehiclesApi from '../../services/vehiclesApi';
import VehicleForm from './VehicleForm';
import VehicleTable from './VehicleTable';
import VehicleStats from './VehicleStats';
import TablePagination, { DEFAULT_PAGE_SIZE } from '../TablePagination';

/**
 * VehicleManagement Component
 * Main component for managing vehicles with Add, Update, and Delete operations
 */
export default function VehicleManagement() {
  const { t, translate } = useTranslation();

  // State management
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);     // full list loading
  const [statsLoading, setStatsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [truckTypes, setTruckTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [availableDevices, setAvailableDevices] = useState({
    mifare: [],
    obu_sbu: []
  });
  const [colors, setColors] = useState([]);
  const [quickStats, setQuickStats] = useState(null); // summary from API response

  // Initial data load
  useEffect(() => {
    loadInitialData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Load all initial data (vehicles, truck types, devices)
   * Strategy: load stats first (fast), then full vehicle list + others in parallel
   */
  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchVehicles(currentPage, pageSize, searchTerm),
        fetchTruckTypes(),
        fetchAvailableDevices(),
        fetchUsers(),
        fetchColors()
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error(`❌ ${t('vehicles.messages.load_failed')}`);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch vehicles with server-side pagination
   */
  const fetchVehicles = async (page = currentPage, limit = pageSize, search = searchTerm) => {
    try {
      setStatsLoading(true);
      const response = await vehiclesApi.getAll({ page, limit, search });
      const { data, pagination, summary } = response.data;
      setVehicles(data || []);
      if (pagination) {
        setTotalItems(pagination.totalItems);
        setTotalPages(pagination.totalPages);
      }
      if (summary) {
        setQuickStats(summary);
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      throw error;
    } finally {
      setStatsLoading(false);
    }
  };

  /**
   * Fetch active truck types
   */
  const fetchTruckTypes = async () => {
    try {
      const response = await vehiclesApi.getTruckTypes();
      const activeTruckTypes = response.data.filter(tt => tt.active);
      setTruckTypes(activeTruckTypes);
    } catch (error) {
      console.error('Error fetching truck types:', error);
    }
  };

  /**
   * Fetch available devices (Mifare and OBU)
   */
  const fetchAvailableDevices = async () => {
    try {
      const devices = await vehiclesApi.getAvailableDevices();
      setAvailableDevices(devices);
    } catch (error) {
      // If 403 Forbidden, user doesn't have permission - this is OK for general users
      if (error.response?.status === 403) {
        console.log('ℹ️ User does not have permission to view available devices');
        setAvailableDevices({ mifare: [], obu_sbu: [] });
      } else {
        console.error('Error fetching available devices:', error);
      }
    }
  };

  /**
   * Fetch active users for vehicle assignment
   */
  const fetchUsers = async () => {
    try {
      const response = await vehiclesApi.getUsers();
      setUsers(response.data);
    } catch (error) {
      // If 403 Forbidden, user doesn't have permission - this is OK
      if (error.response?.status === 403) {
        console.log('ℹ️ User does not have permission to view users list');
        setUsers([]);
      } else {
        console.error('Error fetching users:', error);
      }
    }
  };

  /**
   * Fetch colors for color display
   */
  const fetchColors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/colors`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setColors(data.data || data || []);
      }
    } catch (error) {
      console.error('Error fetching colors:', error);
    }
  };

  /**
   * Handle form submission for add/update vehicle
   */
  const handleVehicleSubmit = async (vehicleData) => {
    if (submitting) return;

    setSubmitting(true);
    try {
      if (editingVehicle) {
        // Update existing vehicle
        await vehiclesApi.update(editingVehicle.id, vehicleData);
        toast.success(`✅ ${t('vehicles.messages.update_success')}`);
      } else {
        // Create new vehicle
        await vehiclesApi.create(vehicleData);
        toast.success(`✅ ${t('vehicles.messages.add_success')}`);
      }

      // Refresh data
      await fetchVehicles(currentPage, pageSize, searchTerm);
      await fetchAvailableDevices();

      // Reset form
      handleCancelForm();
    } catch (error) {
      console.error('Error submitting vehicle:', error);
      const messageKey = editingVehicle
        ? 'vehicles.messages.update_failed'
        : 'vehicles.messages.add_failed';
      const errorMessage = error.response?.data?.message || error.message;
      toast.error(`❌ ${translate(messageKey, { message: errorMessage })}`);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle edit vehicle button click
   */
  const handleEditVehicle = (vehicle) => {
    setEditingVehicle(vehicle);
    setShowAddForm(true);
  };

  /**
   * Handle delete vehicle
   */
  const handleDeleteVehicle = async (id, licensePlate) => {
    const confirmed = window.confirm(
      translate('vehicles.messages.delete_confirm', { licensePlate })
    );

    if (!confirmed) return;

    try {
      await vehiclesApi.delete(id);
      setVehicles(vehicles.filter(vehicle => vehicle.id !== id));
      toast.success(`✅ ${translate('vehicles.messages.delete_success', { licensePlate })}`);
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      toast.error(`❌ ${t('vehicles.messages.delete_failed')}`);
    }
  };

  /**
   * Handle cancel/close form
   */
  const handleCancelForm = () => {
    setShowAddForm(false);
    setEditingVehicle(null);
  };

  // Re-fetch when page, pageSize, or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loading) {
      fetchVehicles(currentPage, pageSize, searchTerm);
    }
  }, [currentPage, pageSize, searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  // vehicles is already the current page from server
  const paginatedVehicles = vehicles;
  const safePage = currentPage;

  // We no longer block the entire page — stats show first, table shows skeleton

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
          🚗 {t('vehicles.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {t('vehicles.subtitle')}
        </p>
      </div>

      {/* Header Actions */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder={`🔍 ${t('vehicles.search_placeholder')}`}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => {
              setEditingVehicle(null);
              setShowAddForm(!showAddForm);
            }}
            className="bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
          >
            ➕ {t('vehicles.add_vehicle_button')}
          </button>

          <button
            onClick={loadInitialData}
            className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
          >
            🔄 {t('vehicles.refresh')}
          </button>
        </div>
      </div>

      {/* Vehicle Form (Add/Edit) */}
      {showAddForm && (
        <VehicleForm
          vehicle={editingVehicle}
          truckTypes={truckTypes}
          availableDevices={availableDevices}
          users={users}
          onSubmit={handleVehicleSubmit}
          onCancel={handleCancelForm}
          submitting={submitting}
          t={t}
          translate={translate}
        />
      )}

      {/* Statistics Cards — shown immediately from /count (fast), updates after full load */}
      <VehicleStats vehicles={vehicles} quickStats={quickStats} statsLoading={statsLoading} t={t} />

      {/* Vehicles Table */}
      {loading ? (
        <div className="flex flex-col gap-2 animate-pulse">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg" />
          ))}
        </div>
      ) : (
        <VehicleTable
          vehicles={paginatedVehicles}
          colors={colors}
          onEdit={handleEditVehicle}
          onDelete={handleDeleteVehicle}
          t={t}
          translate={translate}
        />
      )}

      {!loading && (
        <TablePagination
          currentPage={safePage}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          itemLabel="vehicles"
        />
      )}
    </div>
  );
}
